import React, { useState, useEffect, useRef, useCallback } from "react";
import * as Mui from "@mui/material";
import { useFetch } from "services/hooks/useFetch";
import { CustomAutoComplete } from "components/CustomComponents/CustomAutoComplete";
import { useForm } from "react-hook-form";
import { customAPI } from "services/utils/CustomAPI";
import { showToast } from "services/utils/Status";
import {
  themes,
  scrollTop,
  StatusType,
  tableRowSize,
  stickyHeader,
  defaultAllSelection,
} from "services/constants";
import { useSelector } from "react-redux";
import { InfoMessage } from "components/CustomComponents/CustomButton";
import { checkEditorPermission } from "services/utils/HelperFunction";
import { Loader } from "components/Loader";
import { ConfirmationDialog } from "components/CustomComponents/ConfirmationDialog";
import {
  earningShiftHeader,
  earningTypeColors,
} from "pages/PayRoll/PayRollHeaders";
import { Typography, Box, Paper } from "@mui/material";
import { CustomFilter } from "components/CustomComponents/CustomFilter";
import { CustomAutoCompleteWithLogo } from "components/CustomComponents/CustomAutoCompleteWithLogo";
import moment from "moment";


export const EarningShift = () => {
  const { register, handleSubmit, reset, setValue, formState } = useForm({
    mode: "onBlur",
  });
  const { errors } = formState;
  const { domain, userScreenAccess, topbarCompanyId, userDetails } =
    useSelector((state) => state.tokenReducer);
  const [departmentId, setDepartmentId] = useState(defaultAllSelection);
  const { result: membersData, setLoad: reCallMemberApi } = useFetch(
    `${domain}list/employee?company_id=${topbarCompanyId}&department_id=${departmentId}&status=Active`
  );
  const [payscheduleDate, setPayscheduleDate] = useState("");
  const [departmentName, setDepartmentName] = useState("All");
  const [endDate, setEndDate] = useState("");
  const [startDate, setStartDate] = useState("");
  let showReprocessButton = true;
  const [showAutoCompleteAvathar, setShowAutoCompleteAvathar] = useState(false);
  const [userName, setUserName] = useState("");
  const [userId, setUserId] = useState(defaultAllSelection);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [shiftDetails, setShiftDetails] = useState([]);
  const [showReprocessDialog, setShowReprocessDialog] = useState(false);
  const containerRef = useRef(null);
  let check_access = checkEditorPermission(
    userScreenAccess?.module_details,
    "Earnings",
    "Shift"
  );

  const {
    result: shiftData,
    loading,
    setLoad: reCallApi,
  } = useFetch(
    `${domain}payroll/earning/shift/earning_details/?company_id=${topbarCompanyId}`
  );

  const { result: departmentsData } = useFetch(
    `${domain}get-department/?user_id=${userDetails.id}`
  );

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  useEffect(() => {
    scrollTop(containerRef);
  }, [page]);

  const handleFilteredDataChange = useCallback(
    (filteredData) => {
      setShiftDetails(filteredData);
      if (page * rowsPerPage >= filteredData.length) setPage(0);
    },
    [page, rowsPerPage]
  );

  useEffect(() => {
    if (topbarCompanyId) {
      reCallApi(true);
    }
  }, [reCallApi, topbarCompanyId]);

  const updateUserName = (event, data) => {
    if (data) {
      setUserName(data?.first_name);
      setUserId(data.user_id);
      setValue("user_id", data.user_id);
      const [day, month, year] = data.last_pay_date.split("-");
      let dateValue = `${year}-${month}-${day}`;
      setPayscheduleDate(dateValue);
    }
  };

  const validateStartDate = (value) => {
    if (!value) {
      return "Start date is required.";
    }
  };

  const validateEndDate = (value) => {
    if (!endDate) {
      return "End date is required.";
    } else if (startDate && endDate < startDate) {
      return "End date cannot be before the start date";
    }
    return true;
  };

  const handleStartDateChange = (event) => {
    const date = event.target.value;
    setStartDate(date);
    setValue("from_date", date);
  };

  const onSubmit = async (data) => {
    if (data) {
      const response = await customAPI(
        `${domain}payroll/reprocess_daily_salary/`,
        "POST",
        {
          start_date: moment(startDate).format("YYYY-MM-DD"),
          end_date: moment(endDate).format("YYYY-MM-DD"),
          user_id: userId,
        }
      );
      if (response.ok) {
        showToast(
          "success",
          "daily payrun reprocess successfully successfully"
        );
        reCallApi(true);
        handleCancelClose();
      }
    }
  };

  const handleEndDateChange = (event) => {
    const date = event.target.value;
    setEndDate(date);
    setValue("to_date", date);
  };

  const handleCancelClose = () => {
    setShowReprocessDialog(false);
    reset();
    setStartDate(null);
    setValue("from_date", null);
    setEndDate(null);
    setValue("to_date", null);
    setUserName("");
    setUserId(null);
    setDepartmentId(defaultAllSelection);
    setDepartmentName("All");
  };

  const updateDepartmentName = (event, data) => {
    if (data) {
      setDepartmentId(data.id);
      setDepartmentName(data.label);
      reCallMemberApi(true);
    }
  };

const getYesterday = () => {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return d.toISOString().split("T")[0]; // YYYY-MM-DD
};

const yesterday = getYesterday();

  return loading ? (
    <Loader />
  ) : (
    <React.Fragment>
      <ConfirmationDialog
        open={showReprocessDialog}
        onClose={handleCancelClose}
        onConfirm={handleSubmit(onSubmit)}
        title={"Daily Payroll Reprocess"}
        showChildren={true}
        cancelText="Cancel"
      >
        <form onSubmit={handleSubmit(onSubmit)} noValidate>
          <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
            {/* 40% — Employee Name label */}
            <Box sx={{ width: "40%" }}>
              <Mui.Typography sx={{ fontWeight: "bold" }}>
                Department Name
              </Mui.Typography>
            </Box>

            {/* 60% — Autocomplete */}
            <Box sx={{ width: "60%" }}>
              <CustomAutoComplete
                dataList={[
                  {
                    id: defaultAllSelection,
                    department_name: "All",
                    label: "All",
                  },
                  ...(departmentsData?.map((obj) => ({
                    ...obj,
                    label: obj.department_name,
                  })) ?? []),
                ]}
                selectedValue={departmentName}
                updateDataFunction={updateDepartmentName}
                placeHolder="Select department name"
              />
            </Box>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
            {/* 40% — Employee Name label */}
            <Box sx={{ width: "40%" }}>
              <Mui.Typography sx={{ fontWeight: "bold" }}>
                Employee Name
              </Mui.Typography>
            </Box>
            {/* 60% — Autocomplete */}
            <Box sx={{ width: "60%" }}>
              <CustomAutoCompleteWithLogo
                dataList={membersData?.map((obj) => ({
                  ...obj,
                  label: obj?.first_name,
                  image: obj?.image_url,
                }))}
                selectedValue={userName}
                updateDataFunction={updateUserName}
                // placeHolder="Select employee name"
                showAvathar={showAutoCompleteAvathar}
                setShowAutoCompleteAvathar={setShowAutoCompleteAvathar}
              />
            </Box>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
            {/* 40% — Employee Name label */}
            <Box sx={{ width: "40%" }}>
              <Mui.Typography sx={{ mb: 1, mt: 0.5, fontWeight: "bold" }}>
                Start Date&nbsp;
              </Mui.Typography>
            </Box>
            {/* 60% — Autocomplete */}
            <Box sx={{ width: "60%" }}>
              <Mui.TextField
                size="small"
                type="date"
                value={startDate}
                id="from_date"
                {...register("from_date", {
                  required: "start date is required.",
                  validate: validateStartDate,
                })}
                error={errors.from_date}
                onChange={handleStartDateChange}
                inputProps={{ min: payscheduleDate, max: yesterday }}
                helperText={errors.from_date && errors.from_date.message}
                InputLabelProps={{
                  shrink: true,
                }}
                fullWidth
              />
            </Box>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
            {/* 40% — Employee Name label */}
            <Box sx={{ width: "40%" }}>
              <Mui.Typography sx={{ mb: 1, mt: 0.5, fontWeight: "bold" }}>
                End Date&nbsp;
              </Mui.Typography>
            </Box>
            {/* 60% — Autocomplete */}
            <Box sx={{ width: "60%" }}>
              <Mui.TextField
                size="small"
                type="date"
                id="to_date"
                value={endDate}
                {...register("to_date", {
                  required: "End date is required.",
                  validate: {
                    dateNotBeforeStart: validateEndDate,
                  },
                })}
                onChange={handleEndDateChange}
                InputLabelProps={{
                  shrink: true,
                }}
                inputProps={{ min: payscheduleDate, max: yesterday }}
                error={errors.to_date}
                helperText={errors.to_date && errors.to_date.message}
                fullWidth
              />
            </Box>
          </Box>
        </form>
      </ConfirmationDialog>
      <Box>
        <Box sx={{ mb: 2 }}>
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
            Shift Earning
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Track employee earnings calculated based on completed shifts and
            assigned work hours.
          </Typography>
        </Box>
        <Paper sx={{ width: "100%", height: "78vh" }}>
          <CustomFilter
            title="Shift Earning Records"
            filterHeaders={earningShiftHeader}
            data={shiftData}
            filteredData={shiftDetails}
            onFilteredDataChange={handleFilteredDataChange}
            screenAccess={check_access}
            showReprocessButton={showReprocessButton}
            setShowReprocessDialog={setShowReprocessDialog}
          />

          <Mui.Grid sx={{ ml: 2, mr: 2 }}>
            <Mui.TableContainer ref={containerRef} sx={{ maxHeight: 590 }}>
              <Mui.Table className="border">
                <Mui.TableHead sx={stickyHeader}>
                  <Mui.TableRow sx={tableRowSize}>
                    {earningShiftHeader?.map((value) => (
                      <Mui.TableCell
                        sx={{
                          color: themes.headLine,
                          fontWeight: "bold",
                        }}
                      >
                        {value?.name}
                      </Mui.TableCell>
                    ))}
                  </Mui.TableRow>
                </Mui.TableHead>
                <Mui.TableBody>
                  {shiftDetails?.length === 0 ? (
                    <InfoMessage dataLength={shiftData} />
                  ) : (
                    shiftDetails
                      ?.slice(
                        page * rowsPerPage,
                        page * rowsPerPage + rowsPerPage
                      )
                      ?.map((value) => (
                        <Mui.TableRow sx={tableRowSize}>
                          {earningShiftHeader.map((head) => (
                            <Mui.TableCell>
                              {head.key === "earning_type" ? (
                                <span
                                  style={StatusType(
                                    value.earning_type,
                                    earningTypeColors
                                  )}
                                >
                                  {value.earning_type}
                                </span>
                              ) : head.key === "basis" ? (
                                value?.basis ?? "N/A"
                              ) : (
                                value[head.key] ?? "0"
                              )}
                            </Mui.TableCell>
                          ))}
                        </Mui.TableRow>
                      ))
                  )}
                </Mui.TableBody>
              </Mui.Table>
            </Mui.TableContainer>
            {shiftDetails?.length > 25 && (
              <Box sx={{ mt: 1 }}>
                <Mui.TablePagination
                  className="custom-pagination"
                  component="div"
                  rowsPerPageOptions={[25, 50, 75, 100]}
                  count={shiftDetails?.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                />
              </Box>
            )}
          </Mui.Grid>
        </Paper>
      </Box>
    </React.Fragment>
  );
};
